/* Sorenzia bag upsell v2 (5 Oct 2026): colour + size choice, add-to-bag, smooth slider arrows.
   Loaded once with the cart drawer; all handlers are delegated so they survive the drawer re-rendering. */
(() => {
  if (window.__szBagUpsell) return;
  window.__szBagUpsell = true;

  const reduceMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- colour + size ---------- */
  const cardData = (card) => {
    // Parsed fresh each time: the drawer can morph a card in place with another product's data.
    try {
      return JSON.parse(card.querySelector('[data-sz-json]').textContent);
    } catch (e) {
      return [];
    }
  };

  const syncSize = (card) => {
    const select = card.querySelector('[data-sz-variant]');
    const button = card.querySelector('[data-sz-add]');
    const yours = card.querySelector('[data-sz-yours]');
    if (button) button.disabled = !select || !select.value;
    if (yours) yours.hidden = !select || select.selectedOptions[0]?.dataset.yours !== 'true';
  };

  const selectColour = (card, index, focus) => {
    const data = cardData(card)[index];
    if (!data) return;
    const swatches = card.querySelectorAll('[data-sz-sw]');
    swatches.forEach((sw) => {
      const on = Number(sw.dataset.szSw) === index;
      sw.setAttribute('aria-checked', on ? 'true' : 'false');
      sw.tabIndex = on ? 0 : -1;
      if (on && focus) sw.focus();
    });

    const name = card.querySelector('[data-sz-colour-name]');
    if (name) name.textContent = data.c;
    const price = card.querySelector('[data-sz-price]');
    if (price) price.textContent = data.p;
    card.querySelectorAll('[data-sz-link]').forEach((a) => (a.href = data.u));

    const img = card.querySelector('.sz-up__media img');
    if (img && data.i && img.getAttribute('src') !== data.i) {
      const next = new Image();
      next.sizes = img.sizes;
      next.srcset = data.s;
      next.src = data.i;
      img.classList.add('is-swapping');
      const swap = () => {
        img.srcset = data.s;
        img.src = data.i;
        img.classList.remove('is-swapping');
      };
      if (next.decode) next.decode().then(swap, swap);
      else next.onload = next.onerror = swap;
    }

    // Rebuild sizes for this colour. Keep the shopper's size, else the size they had picked, else ask.
    const select = card.querySelector('[data-sz-variant]');
    if (!select) return;
    const srcEu = card.closest('[data-sz-up]')?.dataset.eu || '';
    const prevLabel = select.selectedOptions[0]?.value ? select.selectedOptions[0].textContent : '';
    select.textContent = '';
    let chosen = null;
    data.v.forEach(([id, label, eu]) => {
      const opt = document.createElement('option');
      opt.value = String(id);
      opt.textContent = label;
      if (srcEu && String(eu).split('–').includes(srcEu)) {
        opt.dataset.yours = 'true';
        chosen = opt;
      }
      select.appendChild(opt);
    });
    if (!chosen && prevLabel) chosen = [...select.options].find((o) => o.textContent === prevLabel) || null;
    if (chosen) {
      chosen.selected = true;
    } else {
      const ph = document.createElement('option');
      ph.value = '';
      ph.textContent = 'Size';
      ph.disabled = true;
      ph.selected = true;
      select.insertBefore(ph, select.firstChild);
    }
    syncSize(card);
  };

  document.addEventListener('change', (event) => {
    const select = event.target.closest?.('[data-sz-variant]');
    if (!select) return;
    const card = select.closest('[data-sz-card]');
    if (card) syncSize(card);
  });

  document.addEventListener('keydown', (event) => {
    const sw = event.target.closest?.('[data-sz-sw]');
    if (!sw) return;
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const card = sw.closest('[data-sz-card]');
    const all = card.querySelectorAll('[data-sz-sw]');
    const next = (Number(sw.dataset.szSw) + keys[event.key] + all.length) % all.length;
    selectColour(card, next, true);
  });

  /* ---------- slider ---------- */
  // Debounced with a short timer (not requestAnimationFrame, which pauses in background/hidden tabs).
  let timer = 0;
  const schedule = () => {
    if (timer) return;
    timer = setTimeout(() => {
      timer = 0;
      syncAll();
    }, 32);
  };

  const syncNav = (section) => {
    const list = section.querySelector('[data-sz-list]');
    const nav = section.querySelector('[data-sz-nav]');
    if (!list || !nav) return;
    const max = list.scrollWidth - list.clientWidth;
    nav.hidden = max <= 4;
    const prev = nav.querySelector('[data-sz-prev]');
    const next = nav.querySelector('[data-sz-next]');
    if (prev) prev.disabled = list.scrollLeft <= 4;
    if (next) next.disabled = list.scrollLeft >= max - 4;
  };
  // Re-measure whenever a picks row changes size (e.g. the drawer opening from width 0), not just on scroll.
  const resizeWatch = 'ResizeObserver' in window ? new ResizeObserver(() => schedule()) : null;
  const syncAll = () =>
    document.querySelectorAll('[data-sz-up]').forEach((section) => {
      const list = section.querySelector('[data-sz-list]');
      if (resizeWatch && list && !list.__szWatched) {
        list.__szWatched = true;
        resizeWatch.observe(list);
      }
      syncNav(section);
    });


  const step = (list, dir) => {
    const card = list.querySelector('.sz-up__card');
    if (!card) return;
    const gap = parseFloat(getComputedStyle(list).columnGap) || 10;
    const unit = card.getBoundingClientRect().width + gap;
    const perView = Math.max(1, Math.floor((list.clientWidth - gap) / unit));
    list.scrollBy({ left: dir * unit * perView, behavior: reduceMotion() ? 'auto' : 'smooth' });
  };

  document.addEventListener('scroll', (event) => {
    if (event.target instanceof Element && event.target.matches('[data-sz-list]')) schedule();
  }, { capture: true, passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
  document.addEventListener('transitionend', schedule, true);
  document.addEventListener('animationend', schedule, true);
  document.addEventListener('toggle', schedule, true);

  /* ---------- "Added" confirmation + tier pulse (5 Oct 2026) ----------
     Watches the drawer's bag count. When it goes up and the drawer is open, show a short
     confirmation naming the newest line, and pulse the multi-buy bar if a new tier was reached. */
  let lastQty = null;
  let lastTier = null;
  let toastTimer = 0;
  let pendingAdd = null;
  const tierOf = (q) => (q >= 4 ? 3 : q >= 3 ? 2 : q >= 2 ? 1 : 0);
  const checkBag = () => {
    const drawer = document.getElementById('cart-drawer');
    const mb = drawer?.querySelector('.sz-mb-top [data-sz-mb]');
    const qty = mb ? Number(mb.dataset.qty) : drawer?.querySelector('.sz-bag') ? NaN : 0;
    if (Number.isNaN(qty)) return;
    const prev = lastQty;
    lastQty = qty;
    const prevTier = lastTier;
    lastTier = tierOf(qty);
    const isOpen = drawer?.hasAttribute('open') || drawer?.querySelector('dialog[open]');
    const increased = prev !== null && qty > prev;
    if (increased) pendingAdd = { at: Date.now(), tierUp: prevTier !== null && lastTier > prevTier };
    if (!pendingAdd || !isOpen) return;
    if (Date.now() - pendingAdd.at > 4000) {
      pendingAdd = null;
      return;
    }
    const tierUp = pendingAdd.tierUp;
    pendingAdd = null;
    const name = drawer.querySelector('.sz-bag .cart-items__table-row .cart-items__title')?.textContent?.trim();
    const toast = drawer.querySelector('[data-sz-toast]');
    if (toast) {
      const text = toast.querySelector('[data-sz-toast-text]');
      if (text) text.textContent = name ? `Added to your bag · ${name.split('|')[0].trim()}` : 'Added to your bag';
      toast.hidden = false;
      requestAnimationFrame(() => toast.classList.add('is-on'));
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => {
        toast.classList.remove('is-on');
        setTimeout(() => (toast.hidden = true), 300);
      }, 2200);
    }
    if (mb && tierUp) {
      mb.classList.remove('is-pulse');
      void mb.offsetWidth;
      mb.classList.add('is-pulse');
      setTimeout(() => mb.classList.remove('is-pulse'), 1300);
    }
  };
  let bagTimer = 0;
  const scheduleBag = () => {
    if (bagTimer) return;
    bagTimer = setTimeout(() => {
      bagTimer = 0;
      checkBag();
    }, 60);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', checkBag);
  else checkBag();
  new MutationObserver(scheduleBag).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-qty', 'open'] });

  /* ---------- clicks: swatch, arrows, add ---------- */
  document.addEventListener('click', async (event) => {
    const sw = event.target.closest?.('[data-sz-sw]');
    if (sw) {
      event.preventDefault();
      selectColour(sw.closest('[data-sz-card]'), Number(sw.dataset.szSw), false);
      return;
    }

    const arrow = event.target.closest?.('[data-sz-prev], [data-sz-next]');
    if (arrow) {
      event.preventDefault();
      const list = arrow.closest('[data-sz-up]')?.querySelector('[data-sz-list]');
      if (list) step(list, arrow.hasAttribute('data-sz-next') ? 1 : -1);
      return;
    }

    const button = event.target.closest?.('[data-sz-add]');
    if (!button || button.disabled) return;
    event.preventDefault();
    const card = button.closest('[data-sz-card]');
    const select = card.querySelector('[data-sz-variant]');
    const err = card.querySelector('[data-sz-err]');
    const id = select?.value;
    if (!id) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    card.classList.add('is-adding');
    if (err) err.hidden = true;

    try {
      const actions = window.Shopify && window.Shopify.actions;
      if (actions && typeof actions.updateCart === 'function') {
        const result = await actions.updateCart(
          { lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + id, quantity: 1 }] },
          { event: { context: 'cart' } }
        );
        if (result && result.userErrors && result.userErrors.length) throw new Error(result.userErrors[0].message);
        // The drawer re-renders in place; bring the refreshed picks row back to its first card.
        [250, 700, 1400].forEach((ms) =>
          setTimeout(() => {
            document.querySelectorAll('[data-sz-list]').forEach((list) => (list.scrollLeft = 0));
            syncAll();
          }, ms)
        );
      } else {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const response = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
        });
        if (!response.ok) throw new Error('add failed');
        window.location.reload();
      }
    } catch (error) {
      console.warn('[sz-bag-upsell]', error);
      card.classList.remove('is-adding');
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (err) err.hidden = false;
    }
  });
})();
